import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import {
  useSubscriptions,
  useUpdateSubscriptionStatus,
  useSubscriptionAnalytics,
} from "../../hooks/useSubscriptions";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import ReactApexChart from "react-apexcharts";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";

// Styled components
const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
}));

const SubscriptionList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [intervalFilter, setIntervalFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [adminNote, setAdminNote] = useState("");

  // Fetch subscriptions data
  const { data, isLoading, error, refetch } = useSubscriptions({
    page,
    limit: rowsPerPage,
    status: statusFilter === "all" ? undefined : statusFilter,
    interval: intervalFilter === "all" ? undefined : intervalFilter,
    search: searchTerm || undefined,
    sortBy,
    sortOrder,
  });

  const { data: analyticsData } = useSubscriptionAnalytics();
  const analytics = analyticsData?.data || {};

  const updateStatusMutation = useUpdateSubscriptionStatus();

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleIntervalFilterChange = (event) => {
    setIntervalFilter(event.target.value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleStatusUpdate = (subscription) => {
    setSelectedSubscription(subscription);
    setNewStatus(subscription.isActive ? "cancelled" : "active");
    setStatusDialogOpen(true);
  };

  const handleViewDetails = (subscription) => {
    setSelectedSubscription(subscription);
    setAdminNote(subscription.metadata?.note || "");
    setViewDetailsOpen(true);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setSelectedSubscription(null);
    setNewStatus("");
    setStatusReason("");
  };

  const handleViewDetailsClose = () => {
    setViewDetailsOpen(false);
    setSelectedSubscription(null);
    setAdminNote("");
  };

  const handleStatusSubmit = async () => {
    if (!selectedSubscription) return;

    await updateStatusMutation.mutateAsync({
      id: selectedSubscription._id,
      status: newStatus,
      reason: statusReason,
    });

    handleStatusDialogClose();
  };

  const handleNoteSave = async () => {
    if (!selectedSubscription) return;

    // Optimistically update or re-fetch after mutation
    // In this codebase we can use updateStatusMutation or similar to save metadata
    await updateStatusMutation.mutateAsync({
      id: selectedSubscription._id,
      status: selectedSubscription.isActive ? "active" : "inactive",
      reason: selectedSubscription.cancelReason,
      metadata: { ...selectedSubscription.metadata, note: adminNote }
    });

    handleViewDetailsClose();
  };

  const handleRefresh = () => {
    refetch();
  };

  const getStatusChip = (subscription) => {
    const isActive = subscription.isActive;
    const amount = parseFloat(subscription.amount || 0);
    
    if (!isActive) {
      return (
        <Chip
          label="Cancelled"
          color="error"
          size="small"
          variant="outlined"
        />
      );
    }
    
    if (amount === 0) {
      return (
        <Chip
          label="Free Active"
          color="info"
          size="small"
          variant="outlined"
        />
      );
    }
    
    return (
      <Chip
        label="Paid Active"
        color="success"
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Invalid Date";
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return "N/A";
    return typeof amount === "string" ? amount : `$${amount}`;
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load subscriptions. Please try again.
        </Alert>
        <Button variant="contained" onClick={refetch}>
          Retry
        </Button>
      </Box>
    );
  }

  const subscriptions = data?.data || [];
  const pagination = data?.pagination || {};

  return (
    <Box >


      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Subscriptions",
            value: analytics.totalSubscriptions || 0,
            icon: "/Images/mdi_tick-circle-outline.png",
            color: theme.palette.primary.main,
            subtext: `Paid: ${analytics.paidActiveSubscriptions || 0}, Free: ${analytics.freeActiveSubscriptions || 0}, Cancelled: ${analytics.cancelledSubscriptions || 0}`,
          },
          {
            label: "Billing Intervals",
            value: Object.keys(analytics.intervalDistribution || {}).length,
            icon: "/Images/hugeicons_transaction.png",
            color: theme.palette.info.main,
            subtext: Object.entries(analytics.intervalDistribution || {})
              .map(([key, count]) => {
                const label = key.toLowerCase();
                if (label === 'month' || label === 'monthly') return `Monthly: ${count}`;
                if (label === 'year' || label === 'yearly') return `Yearly: ${count}`;
                if (label === 'one-time' || label === 'onetime') return `One-time: ${count}`;
                return `${key.charAt(0).toUpperCase() + key.slice(1)}: ${count}`;
              })
              .join(", "),
          },
          {
            label: "Total Revenue",
            value: `$${(analytics.revenue?.totalRevenue || 0).toLocaleString()}`,
            icon: "/Images/streamline_subscription-cashflow.png",
            color: theme.palette.success.main,
            subtext: `Avg: $${(analytics.revenue?.averageTransaction || 0).toFixed(2)}`,
          },
          {
            label: "Top 3 Users",
            value: analytics.topUsers?.length > 0 ? (
              <Box component="span" sx={{ fontSize: '0.9rem' }}>
                {analytics.topUsers.map((u, idx) => (
                  <Typography key={idx} variant="body2" sx={{ fontWeight: 'bold', display: 'block' }}>
                    {u.name}
                  </Typography>
                ))}
              </Box>
            ) : "N/A",
            icon: "/Images/mdi_user-add.png",
            color: theme.palette.warning.main,
            subtext: analytics.topUsers?.length > 0 ? `Total: ${analytics.topUsers.length}` : "No users found",
          },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <StatsCard
              sx={{
                bgcolor: "transparent",
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(2),
                borderRadius: 2,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stat.subtext}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: stat.color,
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  ml: 2,
                }}
              >
                <img
                  src={stat.icon}
                  alt={stat.label}
                  style={{ width: 28, height: 28 }}
                />
              </Box>
            </StatsCard>
          </Grid>
        ))}
      </Grid>

      {/* Analytics Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.default', border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Status Distribution</Typography>
              <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {analytics.paidActiveSubscriptions !== undefined ? (
                  <ReactApexChart
                    key={`status-distribution-${analytics.paidActiveSubscriptions}-${analytics.freeActiveSubscriptions}-${analytics.cancelledSubscriptions}`}
                    options={{
                      chart: { type: 'donut' },
                      labels: ['Paid Active', 'Free Active', 'Cancelled'],
                      colors: [theme.palette.success.main, theme.palette.info.main, theme.palette.error.main],
                      legend: {
                        position: 'bottom',
                        labels: { colors: theme.palette.text.primary }
                      },
                      stroke: { show: false },
                      dataLabels: { enabled: true }
                    }}
                    series={[
                      analytics.paidActiveSubscriptions || 0, 
                      analytics.freeActiveSubscriptions || 0, 
                      analytics.cancelledSubscriptions || 0
                    ]}
                    type="donut"
                    height="100%"
                  />
                ) : (
                  <CircularProgress size={40} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.default', border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Interval Distribution</Typography>
              <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {analytics.series?.intervalDistribution ? (
                  <ReactApexChart
                    key={`interval-distribution-${analytics.series.intervalDistribution.length}`}
                    options={{
                      chart: { type: 'donut' },
                      labels: (analytics.series?.intervalDistribution || []).map(d => {
                        const label = d._id?.toLowerCase();
                        if (label === 'month' || label === 'monthly') return 'Monthly';
                        if (label === 'year' || label === 'yearly') return 'Yearly';
                        if (label === 'one-time' || label === 'onetime') return 'One-time';
                        return d._id?.charAt(0).toUpperCase() + d._id?.slice(1) || 'Unknown';
                      }),
                      colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main],
                      legend: {
                        position: 'bottom',
                        labels: { colors: theme.palette.text.primary }
                      },
                      stroke: { show: false },
                      dataLabels: { enabled: true }
                    }}
                    series={analytics.series?.intervalDistribution?.map(d => d.count) || []}
                    type="donut"
                    height="100%"
                  />
                ) : (
                  <CircularProgress size={40} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card
        sx={{
          mb: 3,
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search subscriptions..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="paid-active">Paid Active</MenuItem>
                  <MenuItem value="free-active">Free Active</MenuItem>
                  <MenuItem value="inactive">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Interval</InputLabel>
                <Select
                  value={intervalFilter}
                  onChange={handleIntervalFilterChange}
                  label="Interval"
                >
                  <MenuItem value="all">All Intervals</MenuItem>
                  <MenuItem value="one-time">One-time</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Rows per page</InputLabel>
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange} label="Rows per page">
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {subscriptions.length} of {pagination.total || 0}{" "}
          subscriptions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Page {pagination.page || 1} of {pagination.pages || 1}
        </Typography>
      </Box>

      {/* Subscriptions Table */}
      <TableContainer
        component={Box}
        variant="outlined"
        sx={{
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '3px'
        }}
      >
        <Table>
          <TableHead
            sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <TableRow>
              <TableCell>
                Plan
              </TableCell>
              <TableCell>
                User
              </TableCell>
              <TableCell>
                Amount
              </TableCell>
              <TableCell>
                Interval
              </TableCell>
              <TableCell>
                Start Date
              </TableCell>
              <TableCell>
                Status
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    No subscriptions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((subscription) => (
                <TableRow key={subscription._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar src={subscription.user?.avatar} sx={{ width: 32, height: 32 }}>
                        {subscription.user?.name?.charAt(0) || subscription.planName?.charAt(0) || "P"}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {subscription.planName || "Unknown Plan"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Type: {subscription.type || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {subscription.user?.name || "Unknown User"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subscription.user?.email || subscription.userId || "N/A"}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/users?userId=${subscription.userId}&tab=subscriptions`)}
                        sx={{ ml: 0.5, color: theme.palette.primary.main }}
                        title="View User Details"
                      >
                        <ArrowForwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatAmount(subscription.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.interval === 'one-time' ? 'One-time' : subscription.interval === 'monthly' || subscription.interval === 'month' ? 'Monthly' : subscription.interval === 'yearly' || subscription.interval === 'year' ? 'Yearly' : "N/A"}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(subscription.startDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(subscription)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleStatusUpdate(subscription)}
                      color={subscription.isActive ? "error" : "success"}
                      title={
                        subscription.isActive
                          ? "Cancel Subscription"
                          : "Activate Subscription"
                      }
                    >
                      {subscription.isActive ? <DeleteIcon /> : <EditIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(subscription)}
                      color="primary"
                      title="View Details"
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Subscription Status</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to{" "}
            {newStatus === "active" ? "activate" : "cancel"} this subscription?
          </Typography>
          {selectedSubscription && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                <strong>Plan:</strong> {selectedSubscription.planName}
              </Typography>
              <Typography variant="body2">
                <strong>User:</strong> {selectedSubscription.userId}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong>{" "}
                {formatAmount(selectedSubscription.amount)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder="Enter reason for status change..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleStatusDialogClose}
            disabled={updateStatusMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStatusSubmit}
            variant="contained"
            color={newStatus === "active" ? "success" : "error"}
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading
              ? "Updating..."
              : `Mark as ${newStatus}`}
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsOpen}
        onClose={handleViewDetailsClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 2 }}>
          Subscription Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedSubscription && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">PLAN INFORMATION</Typography>
                <Box sx={{ mt: 1, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="body1"><strong>Name:</strong> {selectedSubscription.planName}</Typography>
                  <Typography variant="body1"><strong>Type:</strong> {selectedSubscription.type}</Typography>
                  <Typography variant="body1"><strong>Amount:</strong> {formatAmount(selectedSubscription.amount)}</Typography>
                  <Typography variant="body1"><strong>Interval:</strong> {selectedSubscription.interval}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">USER INFORMATION</Typography>
                <Box sx={{ mt: 1, p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="body1"><strong>Name:</strong> {selectedSubscription.user?.name}</Typography>
                  <Typography variant="body1"><strong>Email:</strong> {selectedSubscription.user?.email}</Typography>
                  <Typography variant="body1"><strong>User ID:</strong> {selectedSubscription.userId}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">STATUS & DATES</Typography>
                <Box sx={{ mt: 1, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="body1">
                    <strong>Status:</strong> {
                      !selectedSubscription.isActive ? "Cancelled" : 
                      parseFloat(selectedSubscription.amount || 0) === 0 ? "Free Active" : 
                      "Paid Active"
                    }
                  </Typography>
                  <Typography variant="body1"><strong>Start Date:</strong> {formatDate(selectedSubscription.startDate)}</Typography>
                  {selectedSubscription.cancelDate && (
                    <Typography variant="body1"><strong>Cancel Date:</strong> {formatDate(selectedSubscription.cancelDate)}</Typography>
                  )}
                  <Typography variant="body1"><strong>Last Updated:</strong> {formatDate(selectedSubscription.updatedAt)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">PAYMENT DETAILS</Typography>
                <Box sx={{ mt: 1, p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="body1"><strong>Payment ID:</strong> {selectedSubscription.paymentId || 'N/A'}</Typography>
                  <Typography variant="body1"><strong>Org ID:</strong> {selectedSubscription.orgId || 'N/A'}</Typography>
                  <Typography variant="body1"><strong>Org Name:</strong> {selectedSubscription.orgName || 'N/A'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>ADMIN NOTE</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter a private note about this subscription..."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleViewDetailsClose} variant="outlined">Close</Button>
          <Button
            onClick={handleNoteSave}
            variant="contained"
            color="primary"
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading ? "Saving..." : "Save Note"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionList;
